import api from "../api/API";
import { REMINDER_ORIGIN } from "../utils/reminderStatus";

const STORAGE_KEY = "levelup:fila-captura";
const INTERVALO_FLUSH_MS = 30 * 1000;

let flushando = false;
let timer = null;
const listeners = new Set();

function gerarUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  // Fallback para navegadores/contextos sem randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function lerFila() {
  try {
    const bruto = localStorage.getItem(STORAGE_KEY);
    const fila = bruto ? JSON.parse(bruto) : [];
    return Array.isArray(fila) ? fila : [];
  } catch (err) {
    console.error("captureQueue: fila corrompida, reiniciando.", err);
    return [];
  }
}

function gravarFila(fila) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fila));
  } catch (err) {
    console.error("captureQueue: erro ao gravar fila", err);
  }
  notificar();
}

function notificar() {
  const pendentes = lerFila();
  listeners.forEach((listener) => {
    try {
      listener(pendentes);
    } catch (err) {
      console.error("captureQueue: listener falhou", err);
    }
  });
}

export function assinarFila(listener) {
  listeners.add(listener);
  listener(lerFila());
  return () => listeners.delete(listener);
}

export function totalPendentes() {
  return lerFila().length;
}

/**
 * Coloca o lembrete na fila local e devolve o item imediatamente, para a tela
 * renderizar sem esperar a rede. O envio acontece em seguida.
 */
export function enfileirar({
  texto,
  lembrarEm,
  recorrencia = 0,
  origem = REMINDER_ORIGIN.APP,
}) {
  const item = {
    client_uuid: gerarUuid(),
    texto: String(texto || "").trim(),
    lembrar_em: lembrarEm ? new Date(lembrarEm).toISOString() : null,
    // Repeticao sem horario nao tem do que partir.
    recorrencia: lembrarEm ? Number(recorrencia) || 0 : 0,
    origem,
    criado_em: new Date().toISOString(),
    tentativas: 0,
  };

  if (!item.texto) return null;

  gravarFila([...lerFila(), item]);
  return item;
}

export function removerDaFila(clientUuid) {
  gravarFila(lerFila().filter((item) => item.client_uuid !== clientUuid));
}

/**
 * Tenta enviar tudo que esta parado. O client_uuid + UNIQUE no banco tornam o
 * reenvio idempotente, entao repetir e sempre seguro.
 */
export async function flush() {
  if (flushando) return { enviados: 0, pendentes: totalPendentes() };
  if (!navigator.onLine) return { enviados: 0, pendentes: totalPendentes() };

  const fila = lerFila();
  if (!fila.length) return { enviados: 0, pendentes: 0 };

  flushando = true;
  let enviados = 0;

  try {
    for (const item of fila) {
      try {
        await api.post("/lembretes", {
          texto: item.texto,
          client_uuid: item.client_uuid,
          lembrar_em: item.lembrar_em,
          recorrencia: item.recorrencia || 0,
          origem: item.origem,
        });

        removerDaFila(item.client_uuid);
        enviados += 1;
      } catch (error) {
        const status = error?.response?.status;

        // 4xx (fora 429) significa que o item nunca vai passar. Manter na fila
        // travaria todos os lembretes seguintes atras de um registro invalido.
        if (status && status >= 400 && status < 500 && status !== 429) {
          console.error("captureQueue: item descartado pelo servidor", item, error);
          removerDaFila(item.client_uuid);
          continue;
        }

        // Rede/servidor fora: para aqui e tenta tudo de novo depois.
        break;
      }
    }
  } finally {
    flushando = false;
  }

  return { enviados, pendentes: totalPendentes() };
}

/**
 * Idempotente e sem cleanup de proposito: mais de uma tela precisa garantir que
 * a fila esta sendo drenada, e desmontar uma delas nao pode parar o envio das
 * outras. Os listeners vivem enquanto a aba viver.
 */
export function garantirSincronizacaoAutomatica() {
  if (timer) return;

  const disparar = () => {
    flush().catch(() => {});
  };

  const aoFicarVisivel = () => {
    if (document.visibilityState === "visible") disparar();
  };

  window.addEventListener("online", disparar);
  document.addEventListener("visibilitychange", aoFicarVisivel);
  timer = window.setInterval(disparar, INTERVALO_FLUSH_MS);

  disparar();
}
